import axios from 'axios';
import { config } from '../config/environment';
import type { ApiResponse, WhatsAppIntegrationStatus } from '../types/api';

const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 30000,
});

export interface WABAInfo {
  id: string;
  name: string;
  businessId: string;
  businessName: string;
}

export interface PhoneNumberInfo {
  id: string;
  displayPhoneNumber: string;
  verifiedName: string;
  qualityRating: 'GREEN' | 'YELLOW' | 'RED' | 'UNKNOWN';
}

export interface Waba {
  id: string;
  name: string;
  timezoneId: string;
  hasPhoneNumbers: boolean;
  phoneNumbers: PhoneNumberInfo[];
}

export interface OAuthUrlResponse {
  success: boolean;
  url: string;
}

export interface OAuthCallbackResponse {
  success: boolean;
  message: string;
  accessToken: string;
  googleId: string;
  code: string;
  state: string;
  wabas: Waba[];
}

export interface CompleteConnectionResponse {
  success: boolean;
  message: string;
  config: any;
}

export interface ListPhoneNumbersResponse {
  success: boolean;
  phoneNumbers: PhoneNumberInfo[];
  error?: string;
}

export const whatsappService = {
  /**
   * Obtém URL OAuth para autenticação com Facebook
   */
  getOAuthUrl: async (googleId: string): Promise<OAuthUrlResponse> => {
    const response = await api.get('/api/whatsapp/oauth/url', {
      params: { googleId }
    });

    // Backend retorna { success, url }
    return response.data;
  },

  /**
   * Processa callback OAuth e lista WABAs disponíveis
   */
  handleOAuthCallback: async (payload: { code: string; state: string }): Promise<OAuthCallbackResponse> => {
    const response = await api.post('/api/whatsapp/oauth/callback', payload);
    return response.data;
  },

  /**
   * Completa conexão com WABA e phone number selecionados
   */
  completeOAuthConnection: async (payload: {
    googleId: string;
    accessToken: string;
    wabaId: string;
    phoneNumberId: string;
  }): Promise<CompleteConnectionResponse> => {
    const response = await api.post('/api/whatsapp/oauth/complete', payload);
    return response.data;
  },

  /**
   * Desconecta conta OAuth
   */
  disconnectOAuth: async (googleId: string) => {
    const response = await api.post('/api/whatsapp/oauth/disconnect', null, {
      params: { googleId }
    });
    return response.data;
  },

  /**
   * Verifica status da integração WhatsApp Business
   */
  getIntegrationStatus: async (googleId: string): Promise<ApiResponse<WhatsAppIntegrationStatus>> => {
    const response = await api.get('/api/v1/whatsapp/send/integration-status', {
      params: { googleId }
    });
    return response.data;
  }
};
